import { type ReactNode, useEffect, useState } from "react"
import { Platform, ScrollView, TouchableOpacity, View } from "react-native"
import * as Location from "expo-location"
import SpyGlass from "@/assets/icons/spyglass.svg"
import PlaceIcon from "@/assets/icons/place-white.svg"
import { usePropertyAutocomplete } from "@/queries/analysis"
import { debounce } from "lodash"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import BFText from "@/components/standard/BFText"
import { BFTextInput } from "@/components/standard/BFTextInput"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { BFBackButton } from "@/components/standard/BFBackButton"

interface PlaceProps {
  place?: {
    __typename?: "GooglePlaceType"
    address: string
    placeId?: string | null
    types?: Array<string | null> | null
  } | null
  onSelect: (
    placeId: string | null,
    street: string,
    fullAddress: string
  ) => void
}

const Place: React.FC<PlaceProps> = ({ place, onSelect }: PlaceProps) => {
  const firstIndex = place?.address?.indexOf(", ") || -1
  let street: string = place?.address || ""
  let stateCityZip: string = ""
  if (firstIndex >= 0) {
    street = place?.address?.substring(0, firstIndex) || ""
    stateCityZip = place?.address?.substring(firstIndex + 2) || ""
  }

  return (
    <TouchableOpacity
      testID={`place-${place?.placeId}`}
      onPress={() => {
        if (place?.placeId) {
          onSelect(place.placeId, street, place?.address)
        }
      }}
    >
      <View
        className={"flex flex-row h-[65px] mx-6 border-b border-b-warm-grey"}
      >
        <View className="flex items-center justify-center mr-2">
          <PlaceIcon width={18} height={19} />
        </View>
        <View className="flex flex-col flex-1 justify-center">
          <BFText className="font-spacegrotesk text-white text-[14px]">
            {street}
          </BFText>
          <BFText className="font-spacegrotesk text-white text-[14px]">
            {stateCityZip}
          </BFText>
        </View>
      </View>
    </TouchableOpacity>
  )
}

export interface SearchProps {
  title: string
  placeId?: string | null
  onSelectPlace: (
    placeId: string | null,
    street: string,
    fullAddress: string
  ) => void
  children?: ReactNode
}

export default function Search({
  title,
  placeId,
  onSelectPlace,
  children,
}: SearchProps) {
  const [location, setLocation] = useState<string | null>(null)
  const [address, setAddress] = useState("")
  const { data: propertyAutocomplete } = usePropertyAutocomplete(
    address,
    location,
    placeId ?? null
  )
  const insets = useSafeAreaInsets()
  const isDesktop = useIsDesktop()

  useEffect(() => {
    setStatusBarStyle("dark")
    if (placeId) {
      return
    }
    void (async () => {
      const { status } = await Location.requestForegroundPermissionsAsync()
      if (status !== "granted") {
        console.log("Permission to access location was denied")
        return
      }

      try {
        const location =
          (Platform.OS !== "web" &&
            (await Location.getLastKnownPositionAsync())) ||
          (await Location.getCurrentPositionAsync())
        setLocation(`${location.coords.latitude},${location.coords.longitude}`)
      } catch (err) {
        console.warn(err)
      }
    })()
  }, [])

  const updateAddress = (address: string) => {
    if (address.length > 2) {
      setAddress(address)
    }
  }

  const debouncedUpdateAddress = debounce((address: string) => {
    updateAddress(address)
  }, 500)

  const onChangeText = (text: string) => {
    debouncedUpdateAddress(text)
  }

  return (
    <View
      className="flex flex-col flex-1 min-w-[375px]"
      style={{ paddingTop: isDesktop ? 16 : insets.top + 2 }}
    >
      <View className="px-6 flex flex-col pt-4 ">
        <BFBackButton dark={false} />
        <BFText className="text-[24px] font-figtree-bold text-off-white pt-2 mb-2">
          {title}
        </BFText>
      </View>
      <View
        className={`flex px-6`}
        style={{
          marginLeft: insets.left,
          marginRight: insets.right,
        }}
      >
        <BFTextInput
          testID={`${title.toLowerCase().replaceAll(" ", "-")}-search-text-input`}
          placeholder={"Property Address"}
          autoCorrect={false}
          spellCheck={false}
          autoFocus={true}
          onChangeText={onChangeText}
          variant={"formDark"}
          // className="web:focus:outline-none w-full h-[50px] p-[14px] rounded bg-white font-spacegrotesk placeholder:text-warm-grey placeholder:font-spacegrotesk border border-warm-grey text-midnight-blue"
        />

        <View className="absolute top-[16px] right-[40px]">
          <SpyGlass width={17} height={17} />
        </View>
      </View>
      <View className="flex flex-col flex-1 mt-12 border-red">
        {children && !propertyAutocomplete ? (
          children
        ) : (
          <ScrollView keyboardShouldPersistTaps="handled">
            {propertyAutocomplete?.googlePlaces?.map((place) => (
              <Place
                key={place?.placeId}
                place={place}
                onSelect={onSelectPlace}
              />
            ))}
            {propertyAutocomplete?.googlePlaces?.length || 0 ? (
              <View className="flex h-px bg-light-grey mx-6" />
            ) : (
              <></>
            )}
          </ScrollView>
        )}
      </View>
    </View>
  )
}
